import { STRINGS } from 'config';
import React, { Dispatch, useRef, useState } from 'react';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { Vitals } from 'store/reducer/snomed';
import { Container, ErrorText, InputWrapper, Label, StyledInput, StyledSelect, Unit } from './styles';

export function calculateBMI(weightKg: number, heightM: number): number {
  const bmi = (weightKg / (heightM * heightM));
  return parseFloat(bmi.toFixed(1));
}

export function checkAndUpdateBmi(vitals: Vitals, vitalName: string, val: number|null, dispatch: Dispatch<unknown>) {
  const isWeightUpdate = vitalName === 'weight';
  const isHeightUpdate = vitalName === 'height';
  const weight = isWeightUpdate ? val : vitals.weight;
  const height = isHeightUpdate ? val : vitals.height;
  const bothValues = (weight && height);
  const bmi = bothValues ? calculateBMI(weight, height) : null;
  dispatch({
    type: STRINGS.REDUCERS.SNOMED.SET_VITAL,
    payload: { key: 'bmi', val: bmi },
  });
}

interface VitalInputProps {
  label: string;
  placeholder: string;
  vitalName: Exclude<keyof Vitals, 'bloodPressure'>;
  min: number;
  max: number;
  unit: string;
  decimalPlaces: number;
}

export const VitalInput: React.FC<VitalInputProps> = ({ label, placeholder, vitalName, min, max, unit, decimalPlaces }) => {
  const id = `${vitalName}-vital-input`;
  const { snomed: { vitals } } = useAppState();
  const dispatch = useAppDispatch();

  const raw = vitals[vitalName] as number | null;
  const [inputValue, setInputValue] = useState<string>(raw != null ? String(raw) : '');
  const [touched, setTouched] = useState(false);

  // validation checks
  // First, check that the input is a regular decimal or integer (no 'e' or 'E')
  const isPlainNumber = /^-?\d+(\.\d{1,2})?$/.test(inputValue) && !/[eE]/.test(inputValue);
  const parsed = isPlainNumber ? Number(inputValue) : NaN;
  const isNumber = !Number.isNaN(parsed);
  const isTooSmall = isNumber && parsed < min;
  const isTooLarge = isNumber && parsed > max;
  const isValid = isNumber && parsed >= min && parsed <= max;
  const hasError = inputValue !== '' && !isValid;
  const showError = touched && hasError;
  let errorMessage = '';
  if (!isPlainNumber) {
    errorMessage = `${label} must be a number with up to ${decimalPlaces} decimal places`;
  } else if (isTooSmall || isTooLarge) {
    errorMessage = `${label} invalid`;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value;
    raw = raw.replace(/[^0-9.]/g, '');
    const parts = raw.split('.');
    if (parts.length > 2) {
      raw = `${parts[0]}.${parts[1]}`;
    }

    if (parts.length === 2 && decimalPlaces > 0) {
      parts[1] = parts[1].slice(0, decimalPlaces);
      raw = `${parts[0]}.${parts[1]}`;
    } else if (decimalPlaces === 0 && parts.length > 1) {
      [raw] = parts;
    }
    setInputValue(raw);
    const num = Number(raw);
    if (!Number.isNaN(num)) {
      return;
    }
    if (Number.isNaN(num)) {
      dispatch({
        type: STRINGS.REDUCERS.SNOMED.SET_VITAL,
        payload: { key: vitalName, val: num },
      });
    }
  };

  const handleBlur = () => {
    setTouched(true);
    const n: number | null = inputValue === '' ? null : Number(inputValue);
    const val = hasError ? null : n;
    dispatch({
      type: STRINGS.REDUCERS.SNOMED.SET_VITAL,
      payload: { key: vitalName, val },
    });
    checkAndUpdateBmi(vitals, vitalName, val, dispatch);
  };

  return (
    <Container data-automation={`VitalInput-${vitalName}`}>
      <Label htmlFor={id}>{label}</Label>
      <InputWrapper>
        <StyledInput
          data-automation={`VitalInput-${vitalName}-input`}
          hasError={showError}
          type="text"
          id={id}
          name={vitalName}
          min={min}
          max={max}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={() => setTouched(false)}
        />
        <Unit>{unit}</Unit>
      </InputWrapper>
      <ErrorText
        data-automation={`VitalInputError-${vitalName}`}
      >
        {showError ? errorMessage : ''}
      </ErrorText>
    </Container>
  );
};

export const BloodPressureInput: React.FC = () => {
  const {
    snomed: { vitals },
  } = useAppState();
  const dispatch = useAppDispatch();

  const [sysValue, setSysValue] = useState(
    vitals.systolic != null ? String(vitals.systolic) : '',
  );
  const [diaValue, setDiaValue] = useState(
    vitals.diastolic != null ? String(vitals.diastolic) : '',
  );

  const diastolicRef = useRef<HTMLInputElement>(null);
  const [touched, setTouched] = useState(false);
  const isSysInvalid = sysValue !== '' && (!Number.isNaN(Number(sysValue)) && (Number(sysValue) < 80 || Number(sysValue) > 220));
  const isDiaInvalid = diaValue !== '' && (!Number.isNaN(Number(diaValue)) && (Number(diaValue) < 30 || Number(diaValue) > 160));
  const isIncomplete = (sysValue !== '' && diaValue === '') || (sysValue === '' && diaValue !== '');
  const hasError = isSysInvalid || isDiaInvalid || isIncomplete;
  const showError = touched && hasError;
  let errorMessage = '';
  if (isIncomplete) errorMessage = 'Enter both values';
  else if (isSysInvalid) errorMessage = 'Systolic invalid';
  else if (isDiaInvalid) errorMessage = 'Diastolic invalid';

  const handleBlur = (bpReadingType: 'systolic' | 'diastolic') => {
    setTouched(true);
    const values = {
      systolic: sysValue === '' ? null : Number(sysValue),
      diastolic: diaValue === '' ? null : Number(diaValue),
    };
    const hasErrorObject = {
      systolic: isSysInvalid,
      diastolic: isDiaInvalid,
    };
    const val = hasErrorObject[bpReadingType] ? null : values[bpReadingType];
    dispatch({
      type: STRINGS.REDUCERS.SNOMED.SET_VITAL,
      payload: { key: bpReadingType, val },
    });
  };

  const handleSysKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === '/') {
      e.preventDefault();
      diastolicRef.current?.focus();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'dia' | 'sys') => {
    let raw = e.target.value;
    raw = raw.replace(/[^0-9]/g, '');
    const num = Number(raw);
    if (!Number.isNaN(num)) {
      if (type === 'sys') {
        setSysValue(raw);
      } else {
        setDiaValue(raw);
      }
    }
  };

  return (
    <Container data-automation="VitalInput-bloodPressure">
      <Label htmlFor="bp-systolic">Sitting Blood Pressure</Label>
      <InputWrapper>
        <StyledInput
          data-automation="BP-Systolic-Input"
          hasError={showError}
          type="text"
          id="bp-systolic"
          name="bpSystolic"
          min={80}
          max={220}
          placeholder="000"
          value={sysValue}
          onChange={e => handleChange(e, 'sys')}
          onKeyDown={handleSysKeyDown}
          onBlur={() => handleBlur('systolic')}
          onFocus={() => setTouched(false)}
          style={{ width: '6ch', marginRight: 4 }}
        />
        <span>/</span>
        <StyledInput
          data-automation="BP-Diastolic-Input"
          hasError={showError}
          ref={diastolicRef}
          type="number"
          id="bp-diastolic"
          name="bpDiastolic"
          min={30}
          max={160}
          placeholder="00"
          value={diaValue}
          onChange={e => handleChange(e, 'dia')}
          onBlur={() => handleBlur('diastolic')}
          onFocus={() => setTouched(false)}
          style={{ width: '6ch', marginLeft: 4 }}
        />
        <Unit>mmHg</Unit>
      </InputWrapper>
      <ErrorText
        data-automation="BP-Error"
      >
        {showError ? errorMessage : ''}
      </ErrorText>
    </Container>
  );
};

export const PulseRhythmInput: React.FC = () => {
  const options = [
    { label: 'Regular', value: '162999005' },
    { label: 'Regularly Irregular', value: '163001005' },
    { label: 'Irregularly Irregular', value: '163000006' },
  ];

  const vitalName = 'pulseRhythm';
  const id = 'pulseRhythm-vital-dropdown';
  const { snomed: { vitals } } = useAppState();
  const dispatch = useAppDispatch();

  const raw = vitals[vitalName] as string | null;
  const [selectedValue, setSelectedValue] = useState<string>(raw || '');

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setSelectedValue(value);
    dispatch({
      type: STRINGS.REDUCERS.SNOMED.SET_VITAL,
      payload: { key: vitalName, val: value || null },
    });
  };

  return (
    <Container data-automation="VitalDropdown-pulseRhythm">
      <Label htmlFor={id}>Pulse Rhythm</Label>
      <InputWrapper>
        <StyledSelect
          data-automation="VitalDropdown-pulseRhythm-select"
          id={id}
          name={vitalName}
          value={selectedValue}
          onChange={handleChange}
        >
          <option value="">Not specified</option>
          {options?.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </StyledSelect>
      </InputWrapper>
    </Container>
  );
};
